/// <amd-module name="Tfs/Platform/Page" />
import { ProjectVisibility } from "Tfs/Platform/Contracts/Core";
import { IVssPageContext, IVssService, ServiceOptions, Services, VssService } from "VSS/Platform/Context";
import { CustomerIntelligenceEvent } from "VSS/Platform/Contracts/CustomerIntelligence";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";

/**
 * Details for a Project
 */
export interface IProjectData {
    id: string;
    imageUrl?: string;
    name: string;
    url: string;
    visibility: ProjectVisibility;
}

/**
 * Partial infromation about a Project
 */
export type PartialProjectData = Pick<IProjectData, "id" | "name">;

/**
 * Details for a Team
 */
export interface ITeamData {
    id: string;
    name: string;
}

/**
 * Information specific to the TfsPages.
 */
export interface ITfsPageData {
    /**
     * If the request is targetting a specific project this will contain the project details.
     */
    project?: IProjectData;
}

/**
 * Service for getting TFS-centric information about the current page (like project information)
 */
export interface ITfsPageService extends IVssService {
    /**
     * Gets common TFS-specific data related to the current page
     */
    getData(): ITfsPageData | undefined;

    /**
     * Get information about the current active team. Not guaranteed to work outside of horizontal navigation.
     */
    getHorizontalNavigationTeamData(): ITeamData | undefined;
}

class TfsPageService extends VssService implements ITfsPageService {
    private _removeSubscriptions: () => void;

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);

        const telemetryService = pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService.subscribe(this.addTelemetryProperties, "publish");

        this._removeSubscriptions = () => {
            telemetryService.unsubscribe(this.addTelemetryProperties, "publish");
        };
    }

    public _serviceEnd(pageContext: IVssPageContext): void {
        this._removeSubscriptions();
        super._serviceEnd(pageContext);
    }

    public getData(): ITfsPageData | undefined {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        return contributionService.getData<ITfsPageData>("ms.vss-tfs-web.page-data");
    }

    public getHorizontalNavigationTeamData(): ITeamData | undefined {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        return contributionService.getData<ITeamData>("ms.vss-tfs-web.team-context-data-provider");
    }

    private addTelemetryProperties = (event: CustomerIntelligenceEvent): void => {
        const data = this.getData();
        if (data && data.project) {
            event.properties["dataspaceId"] = data.project.id;
            event.properties["dataspaceType"] = "Project";
            event.properties["dataspaceVisibility"] = data.project.visibility.toString();
        }
    };
}

Services.add("ITfsPageService", { serviceFactory: TfsPageService, options: ServiceOptions.Startup });

/**
 * Project information
 */
export interface IExternalProjectData {
    /**
     * Unique identifier (GUID) of the project
     */
    id: string;

    /**
     * Name of the project
     */
    name: string;
}

/**
 * Service exposing TFS data from the current page to external content
 */
class ExternalTfsPageService extends VssService {
    /**
     * Gets information about the currently selected project for the page
     */
    public getProject(): IExternalProjectData | undefined {
        const pageData = this.pageContext.getService<ITfsPageService>("ITfsPageService").getData();
        return pageData && pageData.project
            ? {
                  id: pageData.project.id,
                  name: pageData.project.name
              }
            : undefined;
    }
}

Services.add("ms.vss-tfs-web.tfs-page-data-service", { serviceFactory: ExternalTfsPageService, options: ServiceOptions.Remotable });

/**
 * Gets common TFS-specific data related to the current page
 *
 * @param pageContext Page context
 */
export function getTFSPageData(pageContext: IVssPageContext): ITfsPageData | undefined {
    const tfsPageDataService = pageContext.getService<ITfsPageService>("ITfsPageService");
    if (tfsPageDataService) {
        return tfsPageDataService.getData();
    }
}

export function getTFSProject(pageContext: IVssPageContext): IProjectData | undefined {
    const pageData = getTFSPageData(pageContext);
    return pageData && pageData.project;
}
